import { keys, has, isArray, uniqueId } from "lodash";

const hexColors = ["#1AADFF", "#00E839", "#FF5D07", "#00DAFF", "#F2E205"];

const konvaLineConfig = (zone, options) => {
  return {
    points: zone.vertices.map((point) => [point.ui.x, point.ui.y]).flat(),
    fill: zone.color,
    stroke: "black",
    strokeWidth: 1,
    closed: true,
    lineJoin: "round",
    opacity: 0.8,
    draggable: false,
  };
};

const konvaCirclesConfig = (zone, options) => {
  return {
    fill: zone.color,
    stroke: "black",
    strokeWidth: 1,
    radius: 6,
    opacity: 1,
    draggable: !!options.draggable,
  };
};

const konvaGroupConfig = (options) => {
  return {
    draggable: !!options.draggable,
  };
};

const buildZonesForUI = (settings, options) => {
  let zones = [];

  if (has(settings, "motion") && isArray(settings["motion"])) {
    // new schema
    zones = settings["motion"].map((zone) => {
      return {
        id: zone.id,
        name: zone.name,
        enabled: zone.state == "enabled",
        vertices: zone.vertices.map((point) => {
          return { x: point.x, y: point.y };
        }),
        properties: {
          detection_types: (zone.properties || {})["detection_types"] || [],
          trigger_ids: (zone.properties || {})["trigger_ids"] || [],
        },
      };
    });
    zones.isNewSchema = true;

    zones.toUpdate = [];
    zones.toDelete = [];
  } else {
    // old schema
    zones = keys(settings)
      .filter((key) => key.match(/^zone\d$/))
      .map((key) => settings[key])
      .map((zone) => {
        return {
          name: zone.name,
          enabled: zone.state == 2,
          vertices: keys(zone)
            .filter((key) => key.match(/^vertex\d/))
            .map((key) => zone[key])
            .map((vertex) => {
              return { x: vertex.x, y: vertex.y };
            }),
        };
      });
  }

  zones.forEach((zone, zoneIdx) => {
    zone.vertices.forEach((point) => {
      point.zone = zone;
      point.uniqueId = uniqueId("point-");
      point.ui = {
        x: point.x * options.width,
        y: point.y * options.height,
      };
    });

    zone.uniqueId = uniqueId("zone-");
    zone.color = hexColors[zoneIdx % hexColors.length];
    zone.drawn =
      zone.vertices.filter((point) => point.x > 0 && point.y > 0).length > 0;
    zone.selected = false;
    zone.config = {
      group: konvaGroupConfig(options),
      line: konvaLineConfig(zone, options),
      circles: konvaCirclesConfig(zone, options),
    };
  });

  const drawnZone = zones.find((zone) => zone.drawn);
  if (drawnZone) drawnZone.selected = true;

  zones.options = options;

  return zones;
};

const buildPayloadForSave = (zones, doorbotId) => {
  if (zones.isNewSchema) {
    // new schema
    const mapZone = (zones, includeId) =>
      zones.map((zone) => {
        const mapped = {
          name: zone.name,
          state: zone.enabled ? "enabled" : "disabled",
          vertices: zone.vertices.map((point) => {
            return {
              x: Number(point.x.toFixed(3)),
              y: Number(point.y.toFixed(3)),
            };
          }),
          properties: zone.properties,
        };
        if (includeId) {
          mapped["id"] = zone.id;
        }
        return mapped;
      });

    const mapId = (zones) =>
      zones.map((zone) => {
        return { id: zone.id };
      });

    const toCreate = zones.filter(
      (zone) =>
        !zone.id &&
        zone.vertices.filter((point) => point.x > 0 || point.y > 0).length > 0
    );

    const toDelete = zones.toDelete;

    const toUpdate = zones.toUpdate;
    zones
      .filter((zone) => zone.vertices.updated && zone.id)
      .forEach((zone) => {
        if (toUpdate.indexOf(zone) === -1) {
          toUpdate.push(zone);
        }
      });

    return {
      doorbot_id: doorbotId,
      zone_type: "motion",
      create: mapZone(toCreate, false),
      delete: mapId(toDelete),
      update: mapZone(toUpdate, true),
    };
  } else {
    // old schema
    const motion_zones = {};

    zones.forEach((zone, zoneIdx) => {
      const motion_zone = {
        name: zone.name,
        state: zone.enabled ? 2 : 0,
      };
      zone.vertices.forEach((point, pointIdx) => {
        motion_zone["vertex" + (pointIdx + 1)] = {
          x: Number(point.x.toFixed(3)),
          y: Number(point.y.toFixed(3)),
        };
      });

      motion_zones["zone" + (zoneIdx + 1)] = motion_zone;
    });

    return { firmware_settings: { motion_settings: motion_zones } };
  }
};

const canAddZones = (zones) => {
  if (zones.isNewSchema) {
    return zones.length < 15;
  } else {
    return (
      zones.filter(
        (zone) =>
          zone.vertices.filter((point) => point.x === 0 && point.y === 0)
            .length > 0
      ).length > 0
    );
  }
};

const addZone = (zones) => {
  let zone;
  const vertices = [
    { x: 0.35, y: 0.2 },
    { x: 0.5, y: 0.2 },
    { x: 0.65, y: 0.2 },
    { x: 0.65, y: 0.5 },
    { x: 0.65, y: 0.8 },
    { x: 0.5, y: 0.8 },
    { x: 0.35, y: 0.8 },
    { x: 0.35, y: 0.5 },
  ];

  if (zones.isNewSchema) {
    zone = {
      uniqueId: uniqueId("zone-"),
      name: "Zone " + (zones.length + 1),
      vertices: vertices.map((point) => {
        return { x: point.x, y: point.y };
      }),
      properties: { detection_types: ["motion"], trigger_ids: [] },
      color: hexColors[zones.length % hexColors.length],
    };
    zones.push(zone);

    zone.vertices.forEach((point) => {
      point.zone = zone;
      point.uniqueId = uniqueId("point-");
      point.ui = {};
    });
  } else {
    zone = zones.find((zone) => !zone.drawn);
    zone.vertices.forEach((point, pointIdx) => {
      point.x = vertices[pointIdx].x;
      point.y = vertices[pointIdx].y;
    });
  }

  zone.enabled = true;
  zones.forEach((zone) => (zone.selected = false));
  zone.selected = true;

  zone.vertices.forEach((point) => {
    point.ui.x = point.x * zones.options.width;
    point.ui.y = point.y * zones.options.height;
  });

  zone.config = {
    group: konvaGroupConfig(zones.options),
    line: konvaLineConfig(zone, zones.options),
    circles: konvaCirclesConfig(zone, zones.options),
  };
  zone.drawn = true;

  return zone;
};

const removeZone = (zones, selectedZone) => {
  if (zones.isNewSchema) {
    const removedZones = zones.splice(zones.indexOf(selectedZone), 1);

    if (removedZones.length > 0 && removedZones[0].id) {
      zones.toDelete.push(removedZones[0]);
      if (zones.toUpdate.indexOf(selectedZone) !== -1) {
        zones.toUpdate.splice(zones.toUpdate.indexOf(selectedZone), 1);
      }
    }
  } else {
    selectedZone.vertices.forEach((point) => {
      point.x = 0;
      point.y = 0;
      point.ui = { x: 0, y: 0 };
    });
    selectedZone.selected = false;
    selectedZone.enabled = false;
    selectedZone.drawn = false;
  }

  const zone = zones.find((zone) => zone.drawn);
  if (zone) zone.selected = true;
};

const updateZone = (zones, selectedZone) => {
  if (zones.isNewSchema && selectedZone.id) {
    if (!zones.toUpdate.find((zone) => zone.id === selectedZone.id)) {
      zones.toUpdate.push(selectedZone);
    }
  }
};

const validVertices = (zones) => {
  return !(
    zones.find(
      (zone) =>
        zone.vertices.find(
          (point) => point.x < 0 || point.x > 1 || point.y < 0 || point.y > 1
        ) != null
    ) != null
  );
};

export default {
  buildZonesForUI: (settings, options) => buildZonesForUI(settings, options),
  buildPayloadForSave: (zones, doorbotId) =>
    buildPayloadForSave(zones, doorbotId),
  canAddZones: (zones) => canAddZones(zones),
  addZone: (zones) => addZone(zones),
  removeZone: (zones, selectedZone) => removeZone(zones, selectedZone),
  updateZone: (zones, selectedZone) => updateZone(zones, selectedZone),
  validVertices: (zones) => validVertices(zones),
};
